// Members & website users (signed language): who registered, their homes,
// self-quotes and bookings. Consent is a state with a date, not a checkbox we
// forget. Read-only; the person's record is edited on the contact.
import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@aiw/ui";
import { Page, Panel, Tile } from "../shell/Page";
import { useMembers, type MemberRow } from "../hooks/slice-b";
import { ago, day, m2 } from "../lib/format";

type Filter = "all" | "home" | "self_quoted" | "booked";

const CONSENT: Record<MemberRow["consent"], { word: string; tone: string }> = {
  ok: { word: "Marketing OK", tone: "bg-success/15 text-success" },
  transactional: { word: "Transactional only", tone: "bg-muted text-muted-foreground" },
  withdrawn: { word: "Withdrawn", tone: "bg-warning/15 text-warning" },
};

function homesWords(h: MemberRow["homes"]) {
  if (h.length === 0) return <span className="text-muted-foreground">—</span>;
  const first = h[0]!;
  const text = first.kind === "measured" ? `${h.length} · site-measured ${m2(first.area_m2)}` : `${h.length} · their estimate · ${first.rooms} rooms`;
  return <span className={cn("rounded-full px-2 text-micro", first.kind === "measured" ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground")}>{text}</span>;
}

export function Members() {
  const members = useMembers();
  const [filter, setFilter] = useState<Filter>("all");
  const rows = members.data?.rows ?? [];
  const t = members.data?.totals;
  const counts = { all: rows.length, home: rows.filter((r) => r.homes.length).length, self_quoted: rows.filter((r) => r.self_quotes > 0).length, booked: rows.filter((r) => !!r.bookings).length };
  const visible = rows.filter((r) => filter === "all" || (filter === "home" ? r.homes.length > 0 : filter === "self_quoted" ? r.self_quotes > 0 : !!r.bookings));

  return (
    <Page
      kicker="Grow"
      title="Members & website users"
      lede="Who registered, their homes, self-quotes and bookings. Consent is a state, not a checkbox we forget."
      actions={
        <select aria-label="Filter" value={filter} onChange={(e) => setFilter(e.target.value as Filter)} className="h-8 rounded-md border bg-background px-2 text-sm">
          <option value="all">All · {counts.all}</option>
          <option value="home">With a home · {counts.home}</option>
          <option value="self_quoted">Self-quoted · {counts.self_quoted}</option>
          <option value="booked">Booked · {counts.booked}</option>
        </select>
      }
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile n={t?.members ?? rows.length} label="Members" sub={t ? `${t.this_week} this week` : "registered on the website"} />
        <Tile n={t?.self_quotes ?? counts.self_quoted} label="Self-quotes" sub={t ? `${t.self_quotes_became_leads} became leads` : "indicative estimates"} />
        <Tile n={t?.measured_homes ?? counts.home} label="Measured homes" sub="rooms, areas and stairs on file" />
        <Tile n={t ? `${t.consent_pct} %` : "—"} label="Marketing consent" sub="may hear from us beyond their job" />
      </div>

      <Panel title="Members" meta="name · email and phone · homes · self-quotes · bookings · consent · last seen" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left font-mono text-micro uppercase text-muted-foreground">
              <tr>
                <th className="pb-1 font-medium">Name</th>
                <th className="hidden pb-1 font-medium md:table-cell">Email · phone</th>
                <th className="pb-1 font-medium">Homes</th>
                <th className="pb-1 text-right font-medium">Self-quotes</th>
                <th className="pb-1 font-medium">Bookings</th>
                <th className="pb-1 font-medium">Consent state</th>
                <th className="hidden pb-1 font-medium lg:table-cell">Last seen</th>
                <th className="pb-1"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const c = CONSENT[r.consent];
                return (
                  <tr key={r.id} className="border-t align-top">
                    <td className="py-2 pr-2"><div className="font-medium">{r.name}</div><div className="text-micro text-muted-foreground">{r.suburb} · registered {day(r.registered_at)}</div></td>
                    <td className="hidden py-2 pr-2 md:table-cell"><div className="truncate">{r.email}</div><div className="num text-micro text-muted-foreground">{r.phone}</div></td>
                    <td className="py-2 pr-2">{homesWords(r.homes)}</td>
                    <td className="num py-2 pr-2 text-right">{r.self_quotes}</td>
                    <td className="py-2 pr-2">{r.bookings ?? <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-2 pr-2"><span className={cn("rounded-full px-2 text-micro", c.tone)}>{c.word}{r.consent !== "ok" ? ` · ${day(r.consent_since)}` : ""}</span></td>
                    <td className="hidden py-2 pr-2 text-micro text-muted-foreground lg:table-cell">{ago(r.last_seen)} ago · {r.last_seen_where}</td>
                    <td className="py-2 text-right text-micro">{r.contact_id ? <Link className="text-primary" to={`/contacts/${r.contact_id}`}>360</Link> : <span className="text-muted-foreground">no contact yet</span>}</td>
                  </tr>
                );
              })}
              {visible.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">{rows.length === 0 ? "No members yet." : "Nobody matches that filter."}</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-micro text-muted-foreground">A measured home comes from a site visit; “their estimate” is what the member entered and carries the estimate disclaimer. Consent changes only from the member's own account.</p>
      </Panel>
    </Page>
  );
}
